import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { setAuthToken as setSdkAuthToken } from '../api';
import { setSentryUser, clearSentryUser } from '../lib/sentry';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Iter-46af: Read the non-HttpOnly CSRF cookie set by /auth/login. Axios
// interceptor below mirrors it into the X-CSRF-Token header on every
// state-changing request so the double-submit pattern matches server-side.
const readCsrfCookie = () => {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)ss_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Iter-46af: withCredentials=true sends/receives httpOnly auth cookies.
  // We still send the Bearer header during the migration window so older
  // browser sessions that don't yet have a fresh CSRF cookie keep working.
  const api = axios.create({
    baseURL: `${API_URL}/api`,
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  // Auto-attach X-CSRF-Token on state-changing requests.
  api.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      const csrf = readCsrfCookie();
      if (csrf) {
        config.headers = config.headers || {};
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
    return config;
  });

  useEffect(() => {
    // Keep the typed SDK in lockstep with axios auth state.
    setSdkAuthToken(token);
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
      fetchUser();
    } else {
      // Iter-46af: no legacy token? try the cookie session via /auth/me.
      // The browser will auto-send the ss_access httpOnly cookie if one
      // exists from a prior cookie-based login.
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      setSentryUser(data);
    } catch (e) {
      // 401 from /auth/me means neither cookie nor bearer is valid — sign
      // the user out locally without calling /logout (no session to kill).
      if (token) localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      clearSentryUser();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    // Iter-46ah: three possible responses
    //   1. mfa_required        → user has MFA enrolled, no cookies yet
    //   2. mfa_enrollment_required → grace counter hit 0, must enroll now
    //   3. normal success      → cookies set, optional mfa_grace_remaining
    // The LoginPage handles cases 1 & 2 by collecting a TOTP / enrolling.
    // Cases 1 & 2 do NOT touch local auth state — the SPA stays "logged out"
    // until /auth/mfa/challenge or /auth/mfa/enroll-verify succeeds.
    if (data?.mfa_required || data?.mfa_enrollment_required) {
      return data;
    }
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    setSentryUser(data.user);
    return data;
  };

  // Iter-46ah: called after /auth/mfa/challenge (or enroll-verify) succeeds —
  // the response body has the same shape as a normal login response.
  const completeMfaLogin = (data) => {
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    setSentryUser(data.user);
    return data;
  };

  // Direct auth setter for impersonation
  const setAuth = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
    setSentryUser(userData);
  };

  const register = async (userData) => {
    const { data } = await api.post('/auth/register', userData);
    localStorage.setItem('token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    setSentryUser(data.user);
    return data;
  };

  const logout = async () => {
    // Iter-46af: call /auth/logout so the server clears cookies + revokes
    // the refresh-token row in auth_sessions. Fire-and-forget — if the
    // request fails (already expired token, network blip) we still clear
    // local state.
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore — clearing local state below is the actual sign-out.
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    clearSentryUser();
  };

  const toggleFavouriteStudio = async (studioId) => {
    const { data } = await api.post(`/studios/${studioId}/favourite`);
    setUser(prev => ({ ...prev, favourite_studios: data.favourites }));
    return data;
  };

  // Iter-46an: exposed so impersonation start/stop can re-sync user state
  // after the backend rotates auth cookies for us.
  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, login, register, logout, setAuth, completeMfaLogin,
      toggleFavouriteStudio, api, refreshUser, isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
